import * as Sentry from '@sentry/react';

const DEBOUNCE_MS = 60_000 * 10; // 10 minutes

let pendingFailures: Record<string, unknown>[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

const flush = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  if (!pendingFailures.length) return;

  const failures = pendingFailures;
  pendingFailures = [];

  Sentry.captureMessage('Image failed to load', {
    extra: { failures },
    level: 'warning',
  });
};

/**
 * Reports an <img> load failure to Sentry. Only call this when `src` was actually set.
 * Failures are collected and sent as a single grouped message once 10 minutes pass without
 * a new failure, when the user navigates away (not counting internal routes) or the tab is
 * hidden/closed before the debounce fires, so a page with many broken images doesn't spam
 * Sentry with one event per image.
 */
export const reportBrokenImage = (src: string, extra: Record<string, unknown> = {}) => {
  pendingFailures.push({ src, ...extra });

  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, DEBOUNCE_MS);
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
}
